// src/components/courses/CourseAccessDialog.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useError } from '../../../contexts/ErrorContext';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    TextField,
    Chip,
    Tab,
    Tabs
} from '@mui/material';
import { X, Plus, Search, User, Users } from 'lucide-react';
import { usersApi } from '../../../services/api'
import {
    getAccessList,
    addAccess,
    removeAccess
} from '../../../services/shareService';
import { ENTITY_TYPES } from '../../../services/shareService';

const CourseAccessDialog = ({ open, onClose, course, token }) => {
    const { t } = useTranslation();
    const { showError } = useError();

    // Состояния для вкладок
    const [tabValue, setTabValue] = useState(0);

    // Состояния для данных доступа
    const [accessList, setAccessList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Состояния для поиска пользователей и групп
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // Состояние для сохранения
    const [isSaving, setIsSaving] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    // Загрузка списка доступа при открытии диалога
    useEffect(() => {
        if (open && course && token) {
            loadAccessList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, course, token]);

    // Загрузка списка доступа
    const loadAccessList = async () => {
        setLoading(true);
        try {
            const data = await getAccessList(token, ENTITY_TYPES.COURSE, course.id);
            setAccessList(data);
        } catch (err) {
            
            showError(t('common.share_dialog.error.loading_access'));
        } finally {
            setLoading(false);
        }
    };

    // Обработчик изменения вкладок
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSearchTerm('');
        setSearchResults([]);
    };

    // Обработчик изменения поискового запроса
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Выполнение поиска
    const handleSearch = async () => {
        if (!searchTerm.trim()) return;

        setIsSearching(true);

        try {
            let results;
            if (tabValue === 0) {
                const response = await usersApi.searchUsers(searchTerm, token);
                results = response || [];

                // Фильтруем результаты, исключая уже выбранных пользователей
                // и пользователей, которые уже имеют доступ
                const existingUserIds = accessList
                    .filter(item => item.user)
                    .map(item => item.user.id);

                const selectedUserIds = selectedUsers.map(user => user.id);

                results = results.filter(user =>
                    !existingUserIds.includes(user.id) && !selectedUserIds.includes(user.id)
                );
            } else {
                const response = await usersApi.searchGroups(searchTerm, token);
                results = response || [];

                // Фильтруем результаты, исключая уже выбранные группы
                // и группы, которые уже имеют доступ
                const existingGroupIds = accessList
                    .filter(item => item.group)
                    .map(item => item.group.id);

                const selectedGroupIds = selectedGroups.map(group => group.id);

                results = results.filter(group =>
                    !existingGroupIds.includes(group.id) && !selectedGroupIds.includes(group.id)
                );
            }

            setSearchResults(results);
        } catch (err) {
            
            showError(tabValue === 0
                ? t('common.share_dialog.error.user_search_error')
                : t('common.share_dialog.error.group_search_error')
            );
        } finally {
            setIsSearching(false);
        }
    };

    // Обработчик выбора пользователя или группы
    const handleSelect = (item) => {
        if (tabValue === 0) {
            setSelectedUsers(prev => [...prev, item]);
        } else {
            setSelectedGroups(prev => [...prev, item]);
        }

        setSearchResults(prev => prev.filter(result => result.id !== item.id));
        setSearchTerm('');
    };

    // Обработчик удаления выбранного пользователя или группы
    const handleRemoveSelected = (id) => {
        if (tabValue === 0) {
            setSelectedUsers(prev => prev.filter(user => user.id !== id));
        } else {
            setSelectedGroups(prev => prev.filter(group => group.id !== id));
        }
    };

    // Обработчик добавления доступа
    const handleAddAccess = async () => {
        if (selectedUsers.length === 0 && selectedGroups.length === 0) return;

        setIsSaving(true);

        try {
            await addAccess(
                token,
                ENTITY_TYPES.COURSE,
                course.id,
                selectedUsers.map(user => user.id),
                selectedGroups.map(group => group.id)
            );

            // Обновляем список доступа
            await loadAccessList();

            // Очищаем выбранных пользователей и группы
            setSelectedUsers([]);
            setSelectedGroups([]);

            showError(t('common.share_dialog.sharing_success'), 'success');
        } catch (err) {
            
            showError(t('common.share_dialog.error.sharing'));
        } finally {
            setIsSaving(false);
        }
    };

    // Обработчик удаления доступа
    const handleRemoveAccess = async (accessId) => {
        setIsRemoving(true);

        try {
            await removeAccess(token, ENTITY_TYPES.COURSE, accessId);

            // Обновляем список доступа
            setAccessList(prev => prev.filter(item => item.id !== accessId));

            showError(t('common.share_dialog.access_removed'), 'success');
        } catch (err) {
            
            showError(t('common.share_dialog.error.removing_access'));
        } finally {
            setIsRemoving(false);
        }
    };

    // Фильтрация списка доступа по пользователям и группам
    const usersWithAccess = accessList.filter(item => item.user);
    const groupsWithAccess = accessList.filter(item => item.group);

    return (
        <Dialog
            open={open}
            onClose={!isSaving && !isRemoving ? onClose : undefined}
            maxWidth="md"
            fullWidth
            aria-labelledby="course-access-dialog-title"
        >
            <DialogTitle id="course-access-dialog-title">
                {t('common.share_dialog.title')}
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="subtitle1" gutterBottom>
                    {t('common.share_dialog.description', { name: course?.title })}
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="fullWidth"
                    >
                        <Tab
                            icon={<User className="w-4 h-4 mr-1" />}
                            label={t('common.share_dialog.share_with_users')}
                            iconPosition="start"
                        />
                        <Tab
                            icon={<Users className="w-4 h-4 mr-1" />}
                            label={t('common.share_dialog.share_with_groups')}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Поиск и выбор пользователей/групп */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t(tabValue === 0
                                ? 'common.share_dialog.search_users'
                                : 'common.share_dialog.search_groups'
                            )}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {isSearching ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <IconButton size="small" onClick={handleSearch}>
                                                <Search className="w-4 h-4" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ),
                            }}
                        />
                    </Box>

                    {searchResults.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('common.share_dialog.search_results')}
                            </Typography>
                            <List dense>
                                {searchResults.map(item => (
                                    <ListItem
                                        key={item.id}
                                        button
                                        onClick={() => handleSelect(item)}
                                    >
                                        <ListItemText
                                            primary={tabValue === 0 ? item.username : item.name}
                                            secondary={tabValue === 0 ? item.email : `${item.members_count || 0} ${t('common.members')}`}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => handleSelect(item)}
                                            >
                                                <Plus className="w-4 h-4" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {/* Выбранные пользователи/группы */}
                    {(selectedUsers.length > 0 || selectedGroups.length > 0) && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t(tabValue === 0
                                    ? 'common.share_dialog.selected_users'
                                    : 'common.share_dialog.selected_groups'
                                )}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {tabValue === 0
                                    ? selectedUsers.map(user => (
                                        <Chip
                                            key={user.id}
                                            label={user.username}
                                            onDelete={() => handleRemoveSelected(user.id)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))
                                    : selectedGroups.map(group => (
                                        <Chip
                                            key={group.id}
                                            label={group.name}
                                            onDelete={() => handleRemoveSelected(group.id)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))
                                }
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddAccess}
                                disabled={isSaving}
                                startIcon={isSaving ? <CircularProgress size={16} /> : <Plus className="w-4 h-4" />}
                                sx={{ mt: 2 }}
                            >
                                {isSaving
                                    ? t('common.sharing')
                                    : t('common.share')
                                }
                            </Button>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Текущий список доступа */}
                    <Typography variant="subtitle1" gutterBottom>
                        {t(tabValue === 0
                            ? 'common.share_dialog.users_with_access'
                            : 'common.share_dialog.groups_with_access'
                        )}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <List>
                            {tabValue === 0 ? (
                                usersWithAccess.length > 0 ? (
                                    usersWithAccess.map(item => (
                                        <ListItem key={item.id}>
                                            <ListItemText
                                                primary={item.user_details?.username}
                                                secondary={item.user_details?.email}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => handleRemoveAccess(item.id)}
                                                    disabled={isRemoving}
                                                >
                                                    {isRemoving ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <X className="w-4 h-4" />
                                                    )}
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                ) : (
                                    <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                                        {t('common.share_dialog.no_users')}
                                    </Typography>
                                )
                            ) : (
                                groupsWithAccess.length > 0 ? (
                                    groupsWithAccess.map(item => (
                                        <ListItem key={item.id}>
                                            <ListItemText
                                                primary={item.group_details?.name}
                                                secondary={`${item.group_details?.members_count || 0} ${t('common.members')}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => handleRemoveAccess(item.id)}
                                                    disabled={isRemoving}
                                                >
                                                    {isRemoving ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <X className="w-4 h-4" />
                                                    )}
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                ) : (
                                    <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                                        {t('common.share_dialog.no_groups')}
                                    </Typography>
                                )
                            )}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={isSaving || isRemoving}
                    color="inherit"
                >
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CourseAccessDialog;
